import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Container,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Modal,
  Divider,
  IconButton,
} from "@mui/material";
import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
  WhatsApp,
  Facebook,
  Instagram,
  LinkedIn,
  CheckCircle,
  ErrorOutline,
  Close,
} from "@mui/icons-material";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import { motion, useReducedMotion } from "framer-motion";
import PerspectiveGrid from "./ui/PerspectiveGrid";
import MagneticButton from "./ui/MagneticButton";

const contactChips = [
  { icon: <Phone />, label: "+91 8500501823", href: "tel:+918500501823" },
  { icon: <Email />, label: "slgtdigitalmarketing@gmail.com", href: "mailto:slgtdigitalmarketing@gmail.com" },
  { icon: <LocationOn />, label: "Anakapalli, Visakhapatnam", href: null },
];

const socialLinks = [
  { icon: <Facebook fontSize="small" />, href: "https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/" },
  { icon: <Instagram fontSize="small" />, href: "https://www.instagram.com/slgt_digital_marketing_agency/" },
  { icon: <LinkedIn fontSize="small" />, href: "https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/" },
];

export default function Contact() {
  const form = useRef();
  const prefersReducedMotion = useReducedMotion();
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      newErrors.email = "Invalid email address";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone))
      newErrors.phone = "Enter a valid 10-digit phone number";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields correctly",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await emailjs.sendForm(
        "service_mzjkjyr",
        "template_bam9wwf",
        form.current,
        "Dp7yeBGBSaVSYZ0Hx"
      );
      setPopupMessage("Message sent successfully!");
      setIsSuccess(true);
      setShowPopup(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        company: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Email send error:", error);
      setPopupMessage("Failed to send message. Please try again later.");
      setIsSuccess(false);
      setShowPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-60px" },
          transition: { duration: 0.6, delay },
        };

  return (
    <>
      <Helmet>
        <title>Contact Us | SLGT Digital Marketing Agency</title>
        <meta
          name="description"
          content="Get in touch with SLGT Digital Marketing Agency based in Anakapalle, Visakhapatnam. Contact us for SEO, Google Ads, Website Development, and Social Media services."
        />
        <meta
          name="keywords"
          content="Contact SLGT, SLGT Digital Marketing, Digital Marketing Anakapalle, SEO Services Visakhapatnam, Google Ads Agency, Website Design, SMM"
        />
        <link rel="canonical" href="https://www.slgt.in/contact" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="author" content="SLGT Digital Marketing Agency" />
        <meta property="og:title" content="Contact SLGT | Digital Marketing Experts in Vizag" />
        <meta
          property="og:description"
          content="Connect with SLGT Digital for top-tier marketing services including SEO, Paid Ads, SMM, and Web Design."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.slgt.in/contact" />
        <meta property="og:image" content="https://www.slgt.in/assets/slgt-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact SLGT Digital Marketing" />
        <meta
          name="twitter:description"
          content="Reach out to SLGT for SEO, Google Ads, and full-service marketing solutions."
        />
        <meta name="twitter:image" content="https://www.slgt.in/assets/slgt-logo.png" />

        {/* JSON-LD Schema Markup */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact SLGT Digital Marketing Agency",
            "url": "https://www.slgt.in/contact",
            "description": "Reach out to SLGT Digital for expert digital marketing solutions in Anakapalle and Visakhapatnam.",
            "image": "https://www.slgt.in/assets/slgt-logo.png",
            "publisher": {
              "@type": "Organization",
              "name": "SLGT Digital",
              "url": "https://www.slgt.in",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.slgt.in/assets/slgt-logo.png"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-8500501823",
                "contactType": "Customer Support"
              },
              "sameAs": [
                "https://www.instagram.com/slgt_digital_marketing_agency/",
                "https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/",
                "https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/"
              ]
            }
          }
          `}
        </script>
      </Helmet>

      <Box sx={{ bgcolor: "background.default" }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            bgcolor: "#05050A",
            pt: { xs: 14, md: 16 },
            pb: { xs: 8, md: 10 },
            textAlign: "center",
          }}
        >
          <PerspectiveGrid />
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div {...fadeUp(0)}>
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: "#fff",
                  mb: 2,
                  lineHeight: 1.15,
                  fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
                }}
              >
                Let's Start a{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #FD8112, #FF5F6D)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                  }}
                >
                  Conversation
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.15)}>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  mb: 5,
                  maxWidth: 560,
                  mx: "auto",
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  fontWeight: 500,
                }}
              >
                Have a project in mind or just want to say hi? Our team usually
                replies within a few hours.
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.3)}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                {contactChips.map((chip) => (
                  <Box
                    key={chip.label}
                    component={chip.href ? "a" : "div"}
                    href={chip.href || undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2.25,
                      py: 1,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.06)",
                      backdropFilter: "blur(18px)",
                      color: "rgba(255,255,255,0.85)",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      transition: "border-color 0.2s ease, color 0.2s ease",
                      "&:hover": chip.href
                        ? { borderColor: "#FD8112", color: "#fff" }
                        : {},
                    }}
                  >
                    <Box sx={{ display: "flex", color: "#FD8112", "& svg": { fontSize: 18 } }}>
                      {chip.icon}
                    </Box>
                    {chip.label}
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Container>
        </Box>

        {/* Form + Info Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <motion.div {...fadeUp(0)} style={{ height: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    height: "100%",
                    border: "1px solid rgba(20,20,31,0.08)",
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Send Us a Message
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Fill out the form below and we'll get back to you shortly.
                  </Typography>

                  <form ref={form} onSubmit={handleSubmit} aria-label="Contact form">
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          error={!!errors.name}
                          helperText={errors.name}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          error={!!errors.phone}
                          helperText={errors.phone}
                          inputProps={{
                            maxLength: 10,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            "aria-label": "Phone Number",
                          }}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, "");
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          error={!!errors.email}
                          helperText={errors.email}
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(/\s/g, "");
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          error={!!errors.subject}
                          helperText={errors.subject}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Question"
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          error={!!errors.message}
                          helperText={errors.message}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <MagneticButton
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={isSubmitting}
                          sx={{
                            background: "linear-gradient(135deg, #FD8112, #FF5F6D)",
                            color: "#fff",
                            py: 1.5,
                            fontSize: "1rem",
                          }}
                        >
                          {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "Send Message"
                          )}
                        </MagneticButton>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <motion.div {...fadeUp(0.15)} style={{ height: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    p: { xs: 3, sm: 4, md: 5 },
                    height: "100%",
                    color: "#fff",
                    background: "linear-gradient(135deg, #12121C 0%, #2A2A3D 100%)",
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      top: -80,
                      right: -80,
                      width: 220,
                      height: 220,
                      borderRadius: "50%",
                      background: "radial-gradient(circle, rgba(253,129,18,0.35), transparent 70%)",
                      filter: "blur(10px)",
                      zIndex: 0,
                    }}
                  />

                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      Get in Touch
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.65)", mb: 4 }}>
                      Reach out through any of the channels below.
                    </Typography>

                    <Grid container spacing={3}>
                      {[
                        { icon: <LocationOn />, title: "Our Location", body: "Anakapalli, Visakhapatnam, 531001" },
                        { icon: <Phone />, title: "Phone", body: "+91 8500501823" },
                        { icon: <Email />, title: "Email", body: "slgtdigitalmarketing@gmail.com" },
                        {
                          icon: <AccessTime />,
                          title: "Business Hours",
                          body: (
                            <>
                              Mon - Fri: 9 AM - 6 PM
                              <br />
                              Sat - Sun: Closed
                            </>
                          ),
                        },
                      ].map((item) => (
                        <Grid item xs={12} key={item.title}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 44,
                                height: 44,
                                borderRadius: "14px",
                                flexShrink: 0,
                                bgcolor: "rgba(253,129,18,0.15)",
                                color: "#FD8112",
                              }}
                            >
                              {item.icon}
                            </Box>
                            <Box>
                              <Typography variant="h6" sx={{ fontSize: "1.05rem" }}>
                                {item.title}
                              </Typography>
                              <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
                                {item.body}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 4 }} />

                    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Follow Us</Typography>
                    <Box sx={{ display: "flex", gap: 1.5, mb: 4 }}>
                      {socialLinks.map((social) => (
                        <IconButton
                          key={social.href}
                          component="a"
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.18)",
                            "&:hover": { borderColor: "#FD8112", color: "#FD8112" },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>

                    <MagneticButton
                      component="a"
                      href="https://wa.me/918500501823"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      fullWidth
                      startIcon={<WhatsApp />}
                      sx={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.35)",
                        py: 1.25,
                        boxShadow: "none",
                        "&:hover": {
                          borderColor: "#FD8112",
                          background: "rgba(255,255,255,0.06)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Chat on WhatsApp
                    </MagneticButton>
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        {/* Popup Modal */}
        <Modal open={showPopup} onClose={() => setShowPopup(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              width: { xs: "90%", sm: 400 },
              p: 4,
              boxShadow: 24,
              textAlign: "center",
            }}
          >
            <IconButton
              onClick={() => setShowPopup(false)}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8 }}
              aria-label="Close"
            >
              <Close fontSize="small" />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                bgcolor: isSuccess ? "rgba(46,125,50,0.12)" : "rgba(211,47,47,0.12)",
                color: isSuccess ? "success.main" : "error.main",
              }}
            >
              {isSuccess ? <CheckCircle sx={{ fontSize: 32 }} /> : <ErrorOutline sx={{ fontSize: 32 }} />}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {popupMessage}
            </Typography>
          </Box>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
